import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateSocialMeta } from '../utils/seo';
import { Button } from '../components/ui/button';
import { Instagram } from 'lucide-react';

const WhatsNew = () => {
  const promotions = [
    {
      id: 'simulator-leagues-fall-2026',
      title: 'Leagues',
      description: `Looking for a fun, competitive, and weather-proof way to play golf?

Our simulator leagues offer the perfect setup for every schedule, skill level, and format preference. Whether you want to play a quick 9 holes mid-week or commit to a full 18-hole weekend round, we have a league for you.

Starting November 9th`,
      imageSrc: '/LeaguesPromo.png',
      imageAlt: 'Pin High Golf & Lounge simulator leagues starting November 9th',
      ctaLabel: 'Join Now',
      ctaUrl: '/leagues',
      ctaInternal: true,
    },
    {
      id: 'weller-wednesdays',
      title: 'Weller Wednesdays',
      description: `Every Wednesday!

Tier tasting (a): $25
• Green — Special Reserve Wheat
• Red — Antique 107 Proof
• Blue — Full Proof

Tier tasting (b): $35
• Blue — Full Proof
• White CYPB
• Orange — Single Barrel

Details
• Tasters are a 1 oz pour of each
• Add on 1.5 oz of Reserve & Garage Beer to either tasting for $8`,
      imageSrc: '/WellerWednesdays.png',
      imageAlt: 'Weller Wednesdays whiskey tasting specials at Pin High Golf & Lounge',
      ctaLabel: 'View Our Menu',
      ctaUrl: '/menu',
      ctaInternal: true,
    },
    {
      id: 'book-your-next-event',
      title: 'Book Your Next Event',
      description: `Celebrate with us at Pin High—birthday parties, corporate events, bachelor parties, and more.

Contact
• Call 216-385-4064
• Email kayla@pinhighbay.com`,
      imageSrc: '/BookYourEvent.png',
      imageAlt: 'Book your next event at Pin High Golf & Lounge Bay Village',
      ctaLabel: 'Plan Your Event',
      ctaUrl: '/events',
      ctaInternal: true,
      secondaryCtas: [
        {
          label: 'Call 216-385-4064',
          url: 'tel:2163854064',
        },
        {
          label: 'Email Kayla',
          url: 'mailto:kayla@pinhighbay.com',
        },
      ],
    },
  ];

  const boldDescriptionLines = new Set([
    'Event Details & Logistics',
    "What's Teed Up for the Day",
    'Starting November 9th',
    'Every Wednesday!',
    'Details',
    'Contact',
    'Tier tasting (a): $25',
    'Tier tasting (b): $35',
  ]);

  const renderDescription = (description) =>
    description.split('\n').map((line, index, lines) => {
      if (!line) {
        return <br key={`line-${index}`} />;
      }

      return (
        <React.Fragment key={`line-${index}`}>
          <span className={boldDescriptionLines.has(line) ? 'font-bold text-gray-900' : undefined}>
            {line}
          </span>
          {index < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });

  useEffect(() => {
    document.title = "What's New - Pin High Golf & Lounge | Updates & Specials";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        "See what's new at Pin High Golf & Lounge on the Westside of Cleveland in Bay Village—special events, league updates, promos, and more, curated from our latest Instagram posts."
      );
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/whats-new');
    updateSocialMeta({
      title: "What's New - Pin High Golf & Lounge | Updates & Specials",
      description: "See what's new at Pin High Golf & Lounge on the Westside of Cleveland in Bay Village—special events, league updates, promos, and more, curated from our latest Instagram posts.",
      path: '/whats-new',
    });

    const venueAddress = {
      '@type': 'PostalAddress',
      streetAddress: '27229 Wolf Road',
      addressLocality: 'Bay Village',
      addressRegion: 'OH',
      postalCode: '44140',
      addressCountry: 'US',
    };
    const eventsSchema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Event',
          name: 'Pin High Simulator Leagues',
          description:
            'Fun, competitive, weather-proof simulator leagues for every schedule, skill level, and format preference. Quick 9-hole mid-week options and full 18-hole weekend rounds available.',
          startDate: '2026-11-09',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          eventStatus: 'https://schema.org/EventScheduled',
          location: {
            '@type': 'Place',
            name: 'Pin High Golf & Lounge',
            address: venueAddress,
          },
          image: ['https://pinhighbay.com/LeaguesPromo.png'],
          url: 'https://pinhighbay.com/leagues',
          organizer: {
            '@type': 'Organization',
            name: 'Pin High Bay Village',
            url: 'https://pinhighbay.com',
          },
        },
        {
          '@type': 'Event',
          name: 'Weller Wednesdays',
          description:
            'Weekly Weller whiskey tastings every Wednesday. Tier tasting (a) $25 and Tier tasting (b) $35, with optional Reserve & Garage Beer add-on.',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          eventStatus: 'https://schema.org/EventScheduled',
          eventSchedule: {
            '@type': 'Schedule',
            repeatFrequency: 'P1W',
            byDay: 'https://schema.org/Wednesday',
          },
          location: {
            '@type': 'Place',
            name: 'Pin High Golf & Lounge',
            address: venueAddress,
          },
          image: ['https://pinhighbay.com/WellerWednesdays.png'],
          url: 'https://pinhighbay.com/whats-new',
          organizer: {
            '@type': 'Organization',
            name: 'Pin High Bay Village',
            url: 'https://pinhighbay.com',
          },
          offers: [
            {
              '@type': 'Offer',
              name: 'Tier tasting (a)',
              price: '25',
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
            },
            {
              '@type': 'Offer',
              name: 'Tier tasting (b)',
              price: '35',
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
            },
          ],
        },
      ],
    };
    let eventsScript = document.querySelector('#whats-new-events-schema');
    if (!eventsScript) {
      eventsScript = document.createElement('script');
      eventsScript.id = 'whats-new-events-schema';
      eventsScript.type = 'application/ld+json';
      document.head.appendChild(eventsScript);
    }
    eventsScript.textContent = JSON.stringify(eventsSchema);

    // Load Elfsight Instagram Feed script
    const script = document.createElement('script');
    script.src = 'https://elfsightcdn.com/platform.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      const es = document.querySelector('#whats-new-events-schema');
      if (es) es.remove();
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-600 to-green-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Instagram className="w-16 h-16 mx-auto mb-6 text-green-200" />
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            What&apos;s New at Pin High
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto text-green-50 mb-8">
            Don&apos;t miss league announcements (next season starts November 9th), specials, and events—see the latest
            from our Instagram feed, all in one place.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white hover:bg-gray-100 text-green-600 text-lg px-8 py-6 transition-all duration-300"
          >
            <a
              href="https://www.instagram.com/pinhighbay/"
              target="_blank"
              rel="noopener noreferrer"
            >
              View on Instagram @pinhighbay
            </a>
          </Button>
        </div>
      </section>

      {/* Promotions */}
      {promotions.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-8 text-gray-900 text-center">
              Current Promotions
            </h2>

            <div className="space-y-6">
              {promotions.map((promo) => (
                <div
                  key={promo.id}
                  className="grid md:grid-cols-2 gap-8 items-center border border-gray-200 rounded-xl overflow-hidden shadow-lg"
                >
                  <div className="w-full bg-gray-100">
                    <div className="relative w-full min-h-[360px] md:min-h-[520px] flex items-start justify-center p-2 md:p-4">
                      <img
                        src={promo.imageSrc}
                        alt={promo.imageAlt}
                        className="w-full h-full object-contain object-top"
                        loading="lazy"
                        onError={(e) => {
                          // If the promo image isn't present yet, fall back to a simple neutral background
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-900/10 to-transparent pointer-events-none" />
                    </div>
                  </div>

                  <div className="p-8">
                    {promo.title && (
                      <h3 className="text-3xl font-bold text-gray-900 mb-3">
                        {promo.title}
                      </h3>
                    )}
                    <div className="text-gray-600 text-lg mb-3">
                      {renderDescription(promo.description)}
                    </div>
                    {promo.instruction && (
                      <p className="text-lg font-semibold text-green-600">
                        {promo.instruction}
                      </p>
                    )}
                    <div className="flex flex-col items-start gap-2">
                      <Button
                        asChild
                        size="lg"
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {promo.ctaInternal ? (
                          <Link to={promo.ctaUrl}>{promo.ctaLabel}</Link>
                        ) : (
                          <a
                            href={promo.ctaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {promo.ctaLabel}
                          </a>
                        )}
                      </Button>
                      {promo.secondaryCtas && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {promo.secondaryCtas.map((cta) => (
                            <Button
                              key={cta.url}
                              asChild
                              size="lg"
                              variant="outline"
                              className="border-green-600 text-green-700 hover:bg-green-50"
                            >
                              <a href={cta.url}>{cta.label}</a>
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Instagram Feed Grid */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 text-center">
            Latest from Our Instagram
          </h2>
          
          {/* Elfsight Instagram Feed */}
          <div className="elfsight-app-70f7b6d6-bde5-43cb-afc2-a70f67ae9e5a" data-elfsight-app-lazy></div>
        </div>
      </section>
    </div>
  );
};

export default WhatsNew;
